// ThemeProvider.jsx — top-level provider that keeps the app's CSS variables
// in sync with the user's local sun. Mount once near the root of App.
//
// Responsibilities:
// - Resolve user's coords via navigator.geolocation (cached in localStorage
//   so we don't re-prompt every session)
// - Compute the current solar phase via ./solarPhase
// - Write that phase's palette into `--color-*` CSS vars on <html>
// - Schedule a timer to re-apply on the next phase boundary
// - Expose a React context so UI can read the active phase / manual override
//
// Fallback order: manual override → geolocation → clock-only (IST).
import React from 'react';
import { detectPhase, nextBoundary, phaseLabel } from './solarPhase';
import { PALETTES, paletteToCssVars } from './palettes';

const LS_COORDS   = 'oralzy_theme_coords_v1';
const LS_OVERRIDE = 'oralzy_theme_override_v1';  // 'auto' | 'pre-dawn' | … | 'night'

const ThemeContext = React.createContext({
  phase: 'morning',
  palette: PALETTES.morning,
  override: 'auto',
  setOverride: () => {},
  coords: null,
  label: 'Morning',
  locationStatus: 'unknown', // 'unknown' | 'granted' | 'denied' | 'unavailable'
  requestLocation: () => {},
});

export function useTheme() { return React.useContext(ThemeContext); }

export default function ThemeProvider({ children }) {
  const [coords,     setCoords]     = React.useState(() => readCachedCoords());
  const [override,   setOverrideRaw] = React.useState(() => {
    try { return localStorage.getItem(LS_OVERRIDE) || 'auto'; } catch (_) { return 'auto'; }
  });
  const [phase, setPhase] = React.useState(() =>
    override !== 'auto' ? override : detectPhase(new Date(), coords)
  );
  const [locationStatus, setLocationStatus] = React.useState(coords ? 'granted' : 'unknown');

  // Ask for geolocation once on mount. If the user previously granted, the
  // browser resolves silently — otherwise they see the permission prompt.
  React.useEffect(() => {
    if (coords) return; // already have cached coords
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocationStatus('unavailable');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude, at: Date.now() };
        setCoords(c);
        setLocationStatus('granted');
        try { localStorage.setItem(LS_COORDS, JSON.stringify(c)); } catch (_) {}
      },
      (err) => {
        // Denied or error — fall back to clock-only.
        setLocationStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 24 * 60 * 60 * 1000 }
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Recompute phase on: coords arrival, override change, or next boundary.
  React.useEffect(() => {
    let timer;
    const apply = () => {
      const p = override !== 'auto' ? override : detectPhase(new Date(), coords);
      setPhase(p);
      // Schedule wake-up on the real next boundary (usually hours away).
      if (override === 'auto') {
        const next = nextBoundary(new Date(), coords);
        const ms = Math.max(60_000, next.getTime() - Date.now());
        timer = setTimeout(apply, ms);
      }
    };
    apply();
    return () => { if (timer) clearTimeout(timer); };
  }, [coords, override]);

  // Paint CSS vars on <html> so *all* components that use var(--color-*)
  // pick up the change. Also adds a .theme-<phase> class for selective
  // styling and toggles the color-scheme hint for native form controls.
  React.useEffect(() => {
    const palette = PALETTES[phase] || PALETTES.morning;
    const root = document.documentElement;
    const vars = paletteToCssVars(palette);
    // Apply individually so transitions can animate each var smoothly.
    vars.split(';').map(s => s.trim()).filter(Boolean).forEach(rule => {
      const idx = rule.indexOf(':');
      if (idx < 0) return;
      const key = rule.slice(0, idx).trim();
      const val = rule.slice(idx + 1).trim();
      root.style.setProperty(key, val);
    });
    root.setAttribute('data-theme', phase);
    root.style.colorScheme = palette.scheme;
    // Body background tracks the palette so the underlay doesn't flash
    // white during route transitions.
    document.body.style.background = palette.bg;
    document.body.style.color      = palette.text;
  }, [phase]);

  const setOverride = React.useCallback((next) => {
    setOverrideRaw(next);
    try {
      if (next === 'auto') localStorage.removeItem(LS_OVERRIDE);
      else localStorage.setItem(LS_OVERRIDE, next);
    } catch (_) {}
  }, []);

  const requestLocation = React.useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocationStatus('unavailable');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude, at: Date.now() };
        setCoords(c);
        setLocationStatus('granted');
        try { localStorage.setItem(LS_COORDS, JSON.stringify(c)); } catch (_) {}
      },
      (err) => setLocationStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable'),
      { enableHighAccuracy: false, timeout: 10_000, maximumAge: 0 }
    );
  }, []);

  const value = React.useMemo(() => ({
    phase,
    palette: PALETTES[phase] || PALETTES.morning,
    override,
    setOverride,
    coords,
    label: phaseLabel(phase),
    locationStatus,
    requestLocation,
  }), [phase, override, coords, locationStatus, setOverride, requestLocation]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

function readCachedCoords() {
  try {
    const raw = localStorage.getItem(LS_COORDS);
    if (!raw) return null;
    const c = JSON.parse(raw);
    // Invalidate cache after 30 days or if user moved cities (not checked here).
    if (!c || typeof c.lat !== 'number' || typeof c.lng !== 'number') return null;
    if (Date.now() - (c.at || 0) > 30 * 24 * 60 * 60 * 1000) return null;
    return c;
  } catch (_) { return null; }
}
